import { message, Form } from "antd";
import axios from "axios";
import { useState } from "react";

export const useBookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [form] = Form.useForm();

  const fetchBookings = async () => {
    try {
      const response = await axios.get("/api/bookings/getallbookings");
      setBookings(response.data);
      setFilteredBookings(response.data);
    } catch (error) {
      message.error("Failed to fetch bookings.");
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get("/api/rooms/getallrooms");
      setRooms(response.data);
    } catch (error) {
      message.error("Failed to fetch rooms.");
    }
  };

  const fetchRatePlans = async () => {
    try {
      const response = await axios.get("/api/rateplans/getallrateplans");
      setRatePlans(response.data);
    } catch (error) {
      message.error("Failed to fetch rate plans.");
    }
  };

  const handleFilterSubmit = (allValues) => {
    const {
      bookingId,
      startDate,
      endDate,
      room,
      ratePlan,
      transactionId,
      search,
      stayedDateRange,
      bookedDateRange,
    } = allValues;

    let filtered = bookings;

    // Filter by Booking ID (if provided)
    if (bookingId) {
      filtered = filtered.filter((booking) =>
        booking.bookingId.includes(bookingId)
      );
    }

    // Filter by Start Date (if provided)
    if (startDate) {
      const start = startDate.format("DD-MM-YYYY");
      filtered = filtered.filter((booking) => booking.fromdate === start);
    }

    // Filter by End Date (Check-Out Date) (if provided)
    if (endDate) {
      const end = endDate.format("DD-MM-YYYY");
      filtered = filtered.filter((booking) => booking.todate === end);
    }

    // Filter by Room (if provided)
    if (room) {
      filtered = filtered.filter((booking) =>
        booking.rooms.some((r) => r.room === room)
      );
    }

    // Filter by Rate Plan (if provided)
    if (ratePlan) {
      filtered = filtered.filter((booking) =>
        booking.rooms.some((r) => r.ratePlanName === ratePlan)
      );
    }

    // Filter by Transaction ID (if provided)
    if (transactionId) {
      filtered = filtered.filter((booking) =>
        booking.transactionId.includes(transactionId)
      );
    }

    // Filter by Search (Guest Name, Guest Email, Guest Phone Number)
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (booking) =>
          booking.guestName.toLowerCase().includes(searchLower) ||
          booking.guestEmail.toLowerCase().includes(searchLower) ||
          booking.guestPhoneNumber.toLowerCase().includes(searchLower)
      );
    }

    // Filter by Stayed Date Range (fromdate, if provided)
    if (stayedDateRange && stayedDateRange[0] && stayedDateRange[1]) {
      const start = new Date(stayedDateRange[0]);
      start.setHours(0, 0, 0, 0); // Normalize start date to midnight
      const end = new Date(stayedDateRange[1]);
      end.setHours(23, 59, 59, 999); // Normalize end date to end of day

      filtered = filtered.filter((booking) => {
        const fromdateParts = booking.fromdate.split("-");
        const [day, month, year] = fromdateParts.map(Number);
        const fromDate = new Date(year, month - 1, day); // month is 0-based in JavaScript Date
        return fromDate >= start && fromDate <= end;
      });
    }

    // Filter by Booked Date Range (createdAt, if provided)
    if (bookedDateRange && bookedDateRange[0] && bookedDateRange[1]) {
      const start = new Date(bookedDateRange[0]);
      start.setHours(0, 0, 0, 0); // Normalize start date to midnight
      const end = new Date(bookedDateRange[1]);
      end.setHours(23, 59, 59, 999); // Normalize end date to end of day

      filtered = filtered.filter((booking) => {
        const createdAtDate = new Date(booking.createdAt);
        return createdAtDate >= start && createdAtDate <= end;
      });
    }

    // Set the filtered bookings
    setFilteredBookings(filtered);
  };

  const handleResetFilters = () => {
    form.resetFields(); // Reset all form fields
    setFilteredBookings(bookings); // Reset the filtered bookings to show all
  };

  return {
    fetchBookings,
    fetchRooms,
    fetchRatePlans,
    ratePlans,
    filteredBookings,
    rooms,
    handleFilterSubmit,
    handleResetFilters,
    form,
  };
};
